import React, {useState} from 'react';
import { useNavigate } from 'react-router-dom';
import Linkify from 'react-linkify';
import clsx from 'clsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

import type { IMessage, ICreateShowingRequest } from 'src/services/api';
import { EMessageChannel } from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';
import ReffieTextTooltip from 'src/components/ReffieTextTooltip';
import MessageHeader from 'src/pages/landlord/leads/messages/components/messages-container/MessageHeader';
import MessageSentStatusIcon from 'src/pages/landlord/leads/messages/components/messages-container/MessageSentStatusIcon';
import OpenHouseAutomationModal from 'src/pages/landlord/leads/messages/components/messages-container/OpenHouseAutomationModal';
import { type IRegion, parseMessageLineIntoRegions, EMAIL_REGEX, PHONE_REGEX } from 'src/pages/landlord/leads/messages/components/messages-container/utils/parse-message-line-into-regions';
import CreateOrUpdateTourModal from 'src/components/create-or-update-tour-modal/CreateOrUpdateTourModal';
import { PlaybookUUID } from 'src/playbooks/playbook-templates';
import { useLeadMessagesPageContext } from 'src/pages/landlord/leads/messages/context/hooks';
import { Flex, FlexColumn } from 'src/components/flex';
import convertFileSize from 'src/utils/convert-file-size';
import ContactInfoModal from 'src/pages/landlord/leads/messages/components/messages-container/ContactInfoModal';

import styles from 'src/pages/landlord/leads/messages/components/messages-container/Message.module.css';

dayjs.extend(relativeTime);

interface Props {
    message: IMessage,
}

/**
 * Display a single message
 */
export default function Message({
    message,
}: Props) {
    const navigate = useNavigate();

    const { loggedInUser, lead, showings, attachments } = useLeadMessagesPageContext();

    /**
     * When we have an existin upcoming showing, by default we will edit that showing instead of creating a new one
     */
    const upcomingShowingsForThisLead = showings.filter((showing: IShowing) => {
        return (showing.lead_uuid === lead.uuid &&
            showing.property_id === lead.property_id &&
            showing.start_time > new Date().toISOString());
    });
    const upcomingShowingId = upcomingShowingsForThisLead.length > 0 ? upcomingShowingsForThisLead[0].id : -1;
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [showingAutomationTarget, setShowingAutomationTarget] = useState<ICreateShowingRequest>();
    const [contactInfoModal, setContactInfoModal] = useState<{ type: 'phone' | 'email', value: string } | null>(null);

    const handleCloseOpenHouseAutomationModal = (goToAutomations: boolean) => {
        if (!showingAutomationTarget) {
            throw new Error('showingAutomationTarget is null');
        }
        const propertyId = showingAutomationTarget.property_id;
        setShowingAutomationTarget(undefined);
        if (goToAutomations) {
            const playbookUuid = PlaybookUUID.ManualSendEmailNotBookedShowing;
            const path = '/landlord/inner/playbooks/new';
            const url = `${path}?template_uuid=${playbookUuid}&property_id=${propertyId}`;
            navigate(url);
        }
    };

    const isSystemMessage = message.system_message_type;

    let className;
    if (isSystemMessage) {
        className = [styles.systemMessage];
    } else if (message.direction === 'out') {
        className = [styles.messageFromAgent];
    } else if (message.direction === 'in') {
        className = [styles.messageFromLead];
    }

    let iconBackgroundColor;
    let iconColor;
    let downloadIconColor;
    if (message.direction === 'out') {
        iconBackgroundColor = '#9EFFDD';
        iconColor = '#000000';
        downloadIconColor = '#9EFFDD';
    } else {
        iconBackgroundColor = '#10B981';
        iconColor = '#FFFFFF';
        downloadIconColor = '#ADADAD';
    }

    const messageLines = message.contents.split('\n').map((line: string, i: number) => {
        const regions = parseMessageLineIntoRegions(line, i);
        const spans = regions.map((region: IRegion) => {
            if (region.parsedDate) {
                return <ReffieTextTooltip key={`tooltip-${region.key}`} title="Click to schedule a showing">
                    <span key={region.key} className={styles.nlpTagDate}
                        role="button"
                        onClick={() => setSelectedDate(region.parsedDate?.toISOString() || null)}
                    >{region.text}</span>
                </ReffieTextTooltip>;
            } else if (region.type === 'phone' || region.type === 'email') {
                return <ReffieTextTooltip key={`tooltip-${region.key}`} title={`Click to add this ${region.type === 'phone' ? 'phone number' : 'email address' } to lead`}>
                    <span key={region.key} className={styles.nlpTagDate}
                        role="button"
                        onClick={() => {
                            if (region.type === 'phone' || region.type === 'email') {
                                setContactInfoModal({ type: region.type, value: region.text });
                            }
                        }}
                    >{region.text}</span>
                </ReffieTextTooltip>;
            } else {
                return <span key={region.key}>{region.text}</span>;
            }
        });
        return <div key={`line-${i}`} className={styles.messageLine}>{spans}</div>;
    });

    const handleCloseShowingModal = () => {
        setSelectedDate(null);
        setShowingAutomationTarget(undefined);
    };

    const messageAttachment = attachments?.find((attachment) => attachment.message_uuid === message.uuid);
    const fileExtension = messageAttachment?.mimetype?.split('/')[1].toUpperCase();
    const attachmentType = messageAttachment?.mimetype?.split('/')[0];

    let attachmentUnit = 'bytes';
    if ((messageAttachment && messageAttachment.file_size >= 1024)) {
        attachmentUnit = 'KB';
    }

    const attachmentUrl = messageAttachment?.download_url;

    const isPlaceholder = message.uuid?.includes('placeholder') || false;
    return (
        <div className={clsx(styles.messageBlock, className, isPlaceholder && styles.placeholder)}>
            {showingAutomationTarget && (
                <OpenHouseAutomationModal
                    open={showingAutomationTarget !== null}
                    onClose={handleCloseOpenHouseAutomationModal}
                />
            )}

            {selectedDate && (
                <CreateOrUpdateTourModal
                    open={selectedDate !== null}
                    onClose={handleCloseShowingModal}
                    selectedLeadUuid={lead.uuid}
                    landlordUserId={loggedInUser.id}
                    leads={[lead]}
                    initDateTime={selectedDate}
                    initPropertyId={lead.property_id}
                    showingId={upcomingShowingId === -1 ? undefined : upcomingShowingId}
                    sourceMessageUuid={message.uuid}
                    onCreated={setShowingAutomationTarget}
                />
            )}

            {contactInfoModal && (
                <ContactInfoModal
                    open={contactInfoModal !== null}
                    onClose={() => setContactInfoModal(null)}
                    type={contactInfoModal.type}
                    value={contactInfoModal.value}
                    lead={lead}
                    loggedInUser={loggedInUser}
                />
            )}

            <FlexColumn
                alignItems={isSystemMessage ? 'center' : message.direction === 'in' ? 'start' : 'end'}
                width={500}
                maxWidth="75%"
            >
                <div className={styles.messageContentsOuter}>
                    {!isSystemMessage && <MessageHeader message={message} isPlaceholder={isPlaceholder} />}

                    <div className={styles.messageBody}>
                        <Linkify componentDecorator={(decoratedHref, decoratedText, key) => {
                            if (decoratedText.match(EMAIL_REGEX) || decoratedText.match(PHONE_REGEX)) {
                                return <span key={key}>{decoratedText}</span>;
                            }
                            return (
                                <a key={key} href={decoratedHref} target="_blank" rel="noopener noreferrer">
                                    {decoratedText}
                                </a>
                            );
                        }}
                        >
                            {messageLines}
                        </Linkify>
                    </div>

                    <div className={styles.messageMetadata}>
                        <div className={styles.messageStatus}>
                            {message.direction === 'out' && message.channel === EMessageChannel.SMS && (
                                <MessageSentStatusIcon message={message} />
                            )}
                        </div>
                    </div>
                </div>

                {messageAttachment && (attachmentType === 'image') && (
                    <Box
                        className={styles.imageContainer}
                    >
                        <Box className={styles.imageHeader}>
                            <MessageHeader message={message} isPlaceholder={isPlaceholder} />
                        </Box>
                        <img
                            src={attachmentUrl}
                            alt={messageAttachment.original_filename}
                            width="240"
                            height="240"
                            className={styles.attachmentImage}
                        />
                        <Box className={styles.imageInfo}>
                            <Flex alignItems="center">
                                <Typography variant="caption" className={clsx(styles.attachmentDetails, styles.messageBody, className)}>
                                    {convertFileSize(messageAttachment.file_size)} {attachmentUnit} &#x2022; {fileExtension}
                                </Typography>
                                <Avatar sx={{ bgcolor: '#FAFAFA', background: 'rgba(0, 0, 0, 0.5)' }}>
                                    <Link href={attachmentUrl} download>
                                        <IconButton>
                                            <FileDownloadOutlinedIcon sx={{ color: downloadIconColor }} />
                                        </IconButton>
                                    </Link>
                                </Avatar>
                            </Flex>
                        </Box>
                    </Box>
                )}

                {messageAttachment && (attachmentType !== 'image') && (
                    <div key={messageAttachment.id} className={styles.messageContentsOuter}>
                        <MessageHeader message={message} isPlaceholder={isPlaceholder} />
                        <Flex justifyContent="space-between" mt={1} gap={6}>
                            <Flex gap={2} minWidth={0}>
                                <Flex alignItems="center">
                                    <Avatar sx={{ bgcolor: iconBackgroundColor }}>
                                        <InsertDriveFileIcon sx={{ fontSize: 20, color: iconColor }} />
                                    </Avatar>
                                </Flex>
                                <FlexColumn>
                                    <Typography variant="body2" className={clsx(styles.attachmentName, styles.messageBody)}>
                                        {messageAttachment.original_filename}
                                    </Typography>
                                    <Tooltip title={fileExtension}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                maxWidth: { xs: '80px', sm: '150px', md: '200px' },
                                            }}
                                            className={clsx('ellipsisText', styles.attachmentDetails, styles.messageBody, className)}
                                        >
                                            {convertFileSize(messageAttachment.file_size)} {attachmentUnit} &#x2022; {fileExtension}
                                        </Typography>
                                    </Tooltip>
                                </FlexColumn>
                            </Flex>
                            <Flex alignItems="center">
                                <Link href={attachmentUrl} download>
                                    <IconButton>
                                        <FileDownloadOutlinedIcon sx={{ color: downloadIconColor }} />
                                    </IconButton>
                                </Link>
                            </Flex>
                        </Flex>
                    </div>
                )}
            </FlexColumn>
        </div>
    );
}
